import type { Asset } from '#modules/AssetOverview/AssetOverview.types'
import type {
	AssetCategory,
	ExpenseEstimateDto,
} from '#modules/Budget/types/expense.types'
import type { FC } from 'react'

import { FlowPage } from '@monthio/design-system'
import { notificationService } from '@monthio/react-components'
import { usePageNavigation } from '#modules/AssetExpenses/usePageNavigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { budgetApiClient } from '../../../../api/budgetApiClient'
import { MissingExpenseEstimateAssetLink } from './MissingExpenseEstimateAssetLink'

interface MissingExpenseEstimateAssetLinksPageProps {
	assets: Asset[]
	assetCategory: AssetCategory
	estimates: ExpenseEstimateDto[]
	onCompleted: () => void
}

export const MissingExpenseEstimateAssetLinksPage: FC<
	MissingExpenseEstimateAssetLinksPageProps
> = ({ assetCategory, assets, estimates, onCompleted }) => {
	const { t } = useTranslation()
	const [currentEstimates, setCurrentEstimates] =
		useState<ExpenseEstimateDto[]>(estimates)

	useEffect(() => {
		if (estimates.length === 0) {
			onCompleted()
		}
	}, [estimates.length, onCompleted])

	const submitEstimates = async (
		assetType: AssetCategory,
		_estimates: ExpenseEstimateDto[],
	) => {
		try {
			await budgetApiClient.updateAssetExpenseEstimates(
				_estimates.map(({ id, assetId }) => ({
					expenseEstimateId: id,
					assetId: assetId ?? null,
				})),
				assetType,
			)
		} catch {
			notificationService.error(t('assetExpensesPage-submitEstimates-error'))
		}
	}

	const { currentPage, goToNextPage, goBack } = usePageNavigation(
		currentEstimates.length,
	)

	const updateExpenseEstimates = useCallback(
		(expenseEstimate: ExpenseEstimateDto) => {
			setCurrentEstimates((prev) => {
				const index = prev.findIndex((x) => x.id === expenseEstimate.id)
				if (index === -1) {
					return prev
				}
				const newExpenseEstimates = [...prev]
				newExpenseEstimates[index] = expenseEstimate
				return newExpenseEstimates
			})
		},
		[],
	)

	const pages = useMemo(
		() =>
			currentEstimates.map((expenseEstimate, index) => (
				<MissingExpenseEstimateAssetLink
					key={index}
					expenseEstimate={expenseEstimate}
					assets={assets}
					onChange={updateExpenseEstimates}
				/>
			)),
		[assets, currentEstimates, updateExpenseEstimates],
	)

	// TODO: Remove if not needed
	// if (!currentEstimates) {
	// 	return (
	// 		<FlowPage>
	// 			<FlowPage.Content $isCentered>
	// 				<LoadingScreen />
	// 			</FlowPage.Content>
	// 		</FlowPage>
	// 	)
	// }

	const onSubmit = async () => {
		if (currentPage === currentEstimates.length - 1) {
			await submitEstimates(assetCategory, currentEstimates)
			onCompleted()
		}
		goToNextPage()
	}

	return (
		<FlowPage>
			<FlowPage.Content $spacing="m">
				<FlowPage.Header
					title={t('assetExpenses-missingExpenseEstimateAssetLinksPage-title', {
						currentNumber: currentPage + 1,
						totalNumber: currentEstimates.length,
					})}
					description={t(
						`assetExpenses-missingExpenseEstimateAssetLinksPage-${assetCategory}-subtitle`,
					)}
				/>
				{pages[currentPage]}
			</FlowPage.Content>
			<FlowPage.Footer
				backButton={
					<FlowPage.BackButton onClick={goBack} ariaLabel={t('btn-back')} />
				}
				actions={
					<FlowPage.Cta onClick={onSubmit}>
						{t('assetExpensesPage-continue-button')}
					</FlowPage.Cta>
				}
			/>
		</FlowPage>
	)
}
